import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Eye, AlertCircle } from 'lucide-react';
import { adminFairManagerRequestApi } from '../services/api';
import { FairManagerRequestResponse, FairManagerRequestListResponse } from '../types';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { extractApiError } from '../utils/errorUtils';

export default function AdminFairManagerRequests() {
  const [requests, setRequests] = useState<FairManagerRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FairManagerRequestResponse | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const pageSize = 20;

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: FairManagerRequestListResponse = await adminFairManagerRequestApi.getRequests(
        statusFilter || undefined,
        page,
        pageSize
      );
      setRequests(response.items);
      setTotal(response.total);
      setTotalPages(response.total_pages);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading requests:', apiError);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await adminFairManagerRequestApi.approveRequest(selectedRequest.id);
      setApproveModalOpen(false);
      setSelectedRequest(null);
      await loadRequests();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error approving request:', apiError);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    try {
      setActionLoading(true);
      await adminFairManagerRequestApi.rejectRequest(selectedRequest.id, adminNotes || undefined);
      setRejectModalOpen(false);
      setSelectedRequest(null);
      setAdminNotes('');
      await loadRequests();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error rejecting request:', apiError);
    } finally {
      setActionLoading(false);
    }
  };

  const openViewModal = (request: FairManagerRequestResponse) => {
    setSelectedRequest(request);
    setViewModalOpen(true);
  };

  const openApproveModal = (request: FairManagerRequestResponse) => {
    setSelectedRequest(request);
    setApproveModalOpen(true);
  };

  const openRejectModal = (request: FairManagerRequestResponse) => {
    setSelectedRequest(request);
    setAdminNotes('');
    setRejectModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' },
    };
    const badge = badges[status as keyof typeof badges] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="admin-fair-manager-requests">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="requests-title">Fair Manager Role Requests</h1>
          <p className="text-gray-600">
            Review and manage requests from users who want to become Fair Managers
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 flex items-center" data-testid="requests-error">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
              Filter by Status:
            </label>
            <select
              id="status-filter"
              data-testid="status-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="text-sm text-gray-600" data-testid="requests-total">
            Total: {total} request{total !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12" data-testid="requests-loading">
            <div className="text-gray-600">Loading requests...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && requests.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow" data-testid="requests-empty">
            <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Requests Found</h3>
            <p className="text-gray-600">
              {statusFilter
                ? `No ${statusFilter} requests at this time.`
                : 'No Fair Manager role requests have been submitted yet.'}
            </p>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && requests.length > 0 && (
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" data-testid="requests-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr
                    key={request.id}
                    data-testid="request-row"
                    onClick={() => openViewModal(request)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.user_email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.business_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>{truncateText(decodeHtmlEntities(request.request_reason), 60)}</div>
                      {request.status === 'rejected' && request.admin_notes && (
                        <div className="text-xs text-gray-500 mt-1">
                          Admin: {truncateText(decodeHtmlEntities(request.admin_notes), 40)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(request.requested_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openViewModal(request)}
                        data-testid="view-request-button"
                        className="text-secondary hover:text-teal-900"
                        title="View details"
                      >
                        <Eye className="h-5 w-5 inline" />
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openApproveModal(request)}
                            data-testid="approve-request-button"
                            className="text-green-600 hover:text-green-900"
                            title="Approve request"
                          >
                            <CheckCircle className="h-5 w-5 inline" />
                          </button>
                          <button
                            onClick={() => openRejectModal(request)}
                            data-testid="reject-request-button"
                            className="text-red-600 hover:text-red-900"
                            title="Reject request"
                          >
                            <XCircle className="h-5 w-5 inline" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && requests.length > 0 && (
          <div className="md:hidden space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{request.business_name}</div>
                    <div className="text-sm text-gray-600">{request.user_email}</div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
                <div className="text-sm text-gray-700 mb-3">
                  {truncateText(decodeHtmlEntities(request.request_reason), 100)}
                </div>
                <div className="text-xs text-gray-500 mb-3">
                  Submitted: {new Date(request.requested_at).toLocaleDateString()}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openViewModal(request)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                  >
                    <Eye className="h-4 w-4 inline mr-1" />
                    View
                  </button>
                  {request.status === 'pending' && (
                    <>
                      <button
                        onClick={() => openApproveModal(request)}
                        className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm"
                      >
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Approve
                      </button>
                      <button
                        onClick={() => openRejectModal(request)}
                        className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm"
                      >
                        <XCircle className="h-4 w-4 inline mr-1" />
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* View Details Modal */}
      {viewModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="view-request-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Request Details</h2>
                {getStatusBadge(selectedRequest.status)}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                  <p className="text-gray-900">{selectedRequest.user_email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business/Organization Name</label>
                  <p className="text-gray-900">{selectedRequest.business_name}</p>
                </div>

                {selectedRequest.business_description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
                    <p className="text-gray-900">{decodeHtmlEntities(selectedRequest.business_description)}</p>
                  </div>
                )}

                {selectedRequest.website && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <a
                      href={selectedRequest.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondary hover:underline"
                    >
                      {selectedRequest.website}
                    </a>
                  </div>
                )}

                {selectedRequest.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <p className="text-gray-900">{selectedRequest.phone}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request Reason</label>
                  <p className="text-gray-900 whitespace-pre-wrap">{decodeHtmlEntities(selectedRequest.request_reason)}</p>
                </div>

                {selectedRequest.admin_notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                    <p className="text-gray-900 whitespace-pre-wrap">{decodeHtmlEntities(selectedRequest.admin_notes)}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted</label>
                  <p className="text-gray-900">{new Date(selectedRequest.requested_at).toLocaleString()}</p>
                </div>

                {selectedRequest.reviewed_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reviewed</label>
                    <p className="text-gray-900">
                      {new Date(selectedRequest.reviewed_at).toLocaleString()}
                      {selectedRequest.reviewed_by_email && ` by ${selectedRequest.reviewed_by_email}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {selectedRequest.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        openApproveModal(selectedRequest);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 inline mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        openRejectModal(selectedRequest);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      <XCircle className="h-4 w-4 inline mr-1" />
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {approveModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="approve-request-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Approve Request</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to approve this request from <strong>{selectedRequest.user_email}</strong>?
              This will grant them Fair Manager permissions.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setApproveModalOpen(false)}
                disabled={actionLoading}
                data-testid="cancel-approve-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                data-testid="confirm-approve-button"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {rejectModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="reject-request-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Reject Request</h2>
            <p className="text-gray-700 mb-4">
              Are you sure you want to reject this request from <strong>{selectedRequest.user_email}</strong>?
            </p>
            <div className="mb-6">
              <label htmlFor="admin-notes" className="block text-sm font-medium text-gray-700 mb-1">
                Admin Notes (optional)
              </label>
              <textarea
                id="admin-notes"
                data-testid="admin-notes-textarea"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Reason for rejection (will be shown to the user)"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setRejectModalOpen(false)}
                disabled={actionLoading}
                data-testid="cancel-reject-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                data-testid="confirm-reject-button"
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
